import type {
  StayMapOption,
  StayRoomOption,
  StaySearchParams
} from '../client'

export function formatExactPrice(price: number, currency: string): string {
  // Small amounts (a €5.53 city tax) must keep their cents — `formatPrice`
  // rounds to whole units, which is right for a stay total and wrong here.
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency
    }).format(price)
  } catch {
    return `${currency} ${price.toFixed(2)}`
  }
}

export function formatStayDates(params: StaySearchParams | null | undefined): string | null {
  // The page used to state the dates only inside the free-text search summary,
  // which is a 200-char truncation of the brief — so a user comparing two date
  // ranges across two links had no way to tell which link was which (GET-10085).
  if (!params?.checkin || !params?.checkout) {
    return null
  }
  // `T00:00:00` pins these to LOCAL midnight. A bare `YYYY-MM-DD` parses as UTC
  // midnight, which renders as the previous day for any negative UTC offset —
  // so this deliberately does NOT reuse `formatCancelDate`, which takes a full
  // ISO datetime and would need a lossy round-trip through `toISOString()`.
  const start = new Date(`${params.checkin}T00:00:00`)
  const end = new Date(`${params.checkout}T00:00:00`)
  const fmt = (date: Date, iso: string): string =>
    Number.isNaN(date.getTime())
      ? iso
      : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
  const nights = Math.round((end.getTime() - start.getTime()) / 86_400_000)
  const nightPart = nights > 0 ? ` · ${nights} night${nights === 1 ? '' : 's'}` : ''
  // Count children too — a 2-adult + 1-child search must not render "2 guests"
  // in the header the whole page is anchored on.
  const people = (params.adults ?? 0) + (params.children ?? 0)
  const guests = people > 0 ? ` · ${people} guest${people === 1 ? '' : 's'}` : ''
  return `${fmt(start, params.checkin)} – ${fmt(end, params.checkout)}${nightPart}${guests}`
}

export function formatCancelDate(iso: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric'
  }).format(date)
}

export function refundPolicy(room: StayRoomOption): string {
  if (!room.refundable) {
    return 'Non-refundable'
  }
  const until = room.free_cancel_until ? formatCancelDate(room.free_cancel_until) : null
  return until ? `Free cancellation until ${until}` : 'Free cancellation'
}

// The at-property charge for a matrix cell, or null when there is nothing to
// add. The matrix ORDERS on the guest total (see `guestTotal`) but each cell
// shows the rate, so without this a dearer-looking rate can sit above a cheaper
// one for no visible reason — and a guest can pick a rate whose real cost is
// disclosed nowhere on the page. `0` is "we checked, nothing extra"; null/
// undefined is "we cannot say", and neither earns a line.
export function atPropertyLabel(room: StayRoomOption): string | null {
  const extra = room.excluded_taxes_and_fees
  if (extra === null || extra === undefined || extra <= 0) {
    return null
  }
  // Exact, not rounded: a €5.53 city tax must not render as "+ €6" while the
  // card's own note says €5.53, and a sub-50c fee must not render as "+ €0",
  // which reads as "nothing extra" — the one thing this label must never say.
  const amount = formatExactPrice(extra, room.excluded_taxes_currency ?? room.currency)
  return `+ ${amount} at property`
}

// Which control sent a choice. Both reach the assistant as the same selection,
// so without this nothing records what the owner actually pressed. `direct` is
// the confirmation page opened from its URL rather than from a tap.
export type ChoiceSource = 'room_table' | 'map_popup' | 'direct'

export interface StayChoice {
  option: StayMapOption
  room: StayRoomOption
}

// The card's lead offer as a room row.
function leadRoom(option: StayMapOption): StayRoomOption {
  return {
    offer_id: option.offer_id,
    room_name: null,
    board_name: null,
    price: option.price,
    currency: option.currency,
    refundable: option.refundable,
    free_cancel_until: null,
    excluded_taxes_and_fees: option.excluded_taxes_and_fees ?? null,
    excluded_taxes_currency: option.excluded_taxes_currency ?? null
  }
}

// A listing with no room breakdown gets one row built from its lead offer, so
// choosing it always works.
export function roomsOf(option: StayMapOption): StayRoomOption[] {
  const rooms = option.room_options ?? []
  return rooms.length > 0 ? rooms : [leadRoom(option)]
}

// Mirrors the backend's `find_offer_in_options`, which accepts an option's
// lead offer as well as its rooms. A page whose rooms were re-fetched can leave
// the lead out of `room_options`, and the map's "Choose this" sends the lead.
// A room row wins when both match, because it names the room and board.
export function findChoice(options: StayMapOption[], offerId: string): StayChoice | null {
  for (const option of options) {
    const room = (option.room_options ?? []).find((candidate) => candidate.offer_id === offerId)
    if (room) {
      return { option, room }
    }
    if (option.offer_id === offerId) {
      return { option, room: leadRoom(option) }
    }
  }
  return null
}

export function choiceProperties(choice: StayChoice, source: ChoiceSource): Record<string, unknown> {
  const { option, room } = choice
  return {
    source,
    offer_id: room.offer_id,
    hotel: option.name,
    room_name: room.room_name,
    board_name: room.board_name,
    price: room.price,
    currency: room.currency,
    refundable: room.refundable
  }
}

export function stayListPath(token: string): string {
  return `/stays/${encodeURIComponent(token)}`
}

// The page where the owner confirms a booking. The offer rides in the query
// string, so a reload finds it again in the stored option set.
export function stayConfirmPath(token: string, offerId: string, source: ChoiceSource): string {
  const query = new URLSearchParams({ offer: offerId, source })
  return `${stayListPath(token)}/confirm?${query.toString()}`
}

export function readConfirmParams(params: URLSearchParams): { offerId: string | null, source: ChoiceSource } {
  const source = params.get('source')
  return {
    offerId: params.get('offer'),
    source: source === 'room_table' || source === 'map_popup' ? source : 'direct'
  }
}
